// frontend/src/hooks/useBulkImport.js
// Reusable file-import hook for grid pages. Uploads XLSX/CSV to an entity
// import endpoint (e.g. /api/forecasts/import/) speaking the unified import
// envelope (api.services.table_import):
//   { summary: { total_rows, created?, updated?, error_count, info_count, ... },
//     errors: [{ row, identifier, error_type, message }],
//     info:   [{ row, message }], unknown_identifiers: [...] }
// A processed file always answers 200 with the envelope; only preflight
// refusals (bad file, missing columns) arrive as 4xx with an apiErrors code.

import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../services/SessionApiService';
import { flattenApiError, apiErrorFrom } from '../utils/parseApiError';
import { apiJson, translateRequestError } from '../queries/api';
import { useApiMutation } from '../queries/useApiMutation';

/**
 * @param {Object} opts
 * @param {string} opts.modelName        - entity key for filenames, e.g. 'forecasts'
 * @param {string} opts.endpoint         - the entity's import action, e.g.
 *   '/api/forecasts/import/'. Every merchant-facing import is a dedicated
 *   `/api/<entity>/import/` action (the staff bulk-upload rail has its own
 *   page and hook).
 * @param {string} opts.templateEndpoint - e.g. '/api/forecasts/template/'
 * @param {string[]} opts.changes        - the kinds of store data the import
 *   writes (`DATA` in queries/dataChanged.js); every reader of them, the
 *   page's own grid included, reads again once the file is processed
 * @param {Function} opts.onSuccess      - called after a successful (or partial) upload
 */
export default function useBulkImport({
  modelName, endpoint, templateEndpoint, changes, onSuccess, onReset, extraParams,
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // Blank-cell intent, declared with the file rather than inferred from it:
  // an empty cell reads the same whether the merchant deleted the value or
  // never filled it in. Default false = keep, because a keep that should have
  // been a clear costs a re-upload and the reverse costs the data.
  const [clearBlanks, setClearBlanks] = useState(false);
  const fileInputRef = useRef(null);

  // The upload step is the only async write — file selection / template
  // download / error CSV download are local UX state. Routing the upload
  // through useApiMutation gives us typed errors (ApiError) and pending
  // state without re-implementing the boilerplate.
  const uploadMutation = useApiMutation({
    mutationFn: ({ formData, endpoint }) => apiJson.upload(endpoint, formData),
    changes,
  });
  const loading = uploadMutation.isPending;

  const selectFile = useCallback((selectedFile) => {
    if (!selectedFile) return;
    setResult(null);
    setError(null);

    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'csv'].includes(ext)) {
      setError(t('bulkImport.fileTypeError'));
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError(t('bulkImport.fileTooLarge'));
      return;
    }
    setFile(selectedFile);
  }, [t]);

  const upload = useCallback(async () => {
    if (!file) return;
    setResult(null);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('blank_policy', clearBlanks ? 'clear' : 'keep');
    // Entity-specific upload options (the supplier catalog's target supplier
    // and apply policy). The backend validates these BEFORE reading the file,
    // so a bad value is refused without leaving an audit row.
    Object.entries(extraParams || {}).forEach(([key, value]) => {
      formData.append(key, value);
    });

    let data;
    try {
      data = await uploadMutation.mutateAsync({ formData, endpoint });
    } catch (err) {
      // Preflight refusals carry a structured apiErrors code (file_required,
      // invalid_file, missing_columns, empty_file, ...); anything else gets the
      // generic line with the next step for its cause.
      console.error('import failed:', err.message, err.body);
      setError(translateRequestError(err, t, 'bulkImport.uploadFailed'));
      return;
    }
    // Outside the try: the upload is done, and a caller's own failure here
    // must not read as a failed upload — re-uploading would write the data
    // twice.
    setResult(data);
    if (onSuccess) onSuccess(data);
  }, [file, clearBlanks, extraParams, endpoint, onSuccess, t, uploadMutation]);

  const downloadTemplate = useCallback(async () => {
    if (!templateEndpoint) {
      // Update-only entities have no template endpoint (see
      // ImportTemplateMixin) and the dialog hides the button for them. If this
      // ever runs it is a wiring bug, and a loud one beats GET /undefined.
      throw new Error(
        `useBulkImport(${modelName}): downloadTemplate called with no templateEndpoint.`,
      );
    }
    let res;
    try {
      res = await sessionApi.get(templateEndpoint);
      if (!res.ok) throw await apiErrorFrom(res, `GET ${templateEndpoint}`);
    } catch (err) {
      console.error('template download failed:', err.message);
      setError(translateRequestError(err, t, 'bulkImport.templateFailed'));
      return;
    }
    setError(null);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), {
      href: url,
      download: `${modelName}_template.xlsx`,
    }).click();
    URL.revokeObjectURL(url);
  }, [templateEndpoint, modelName, t]);

  // ONE reset for the whole dialog, including entity-specific options the
  // caller holds, so a destructive option (the supplier catalog's "replace
  // the whole list") never survives a close.
  const reset = useCallback(() => {
    setFile(null);
    setResult(null);
    setError(null);
    setClearBlanks(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (onReset) onReset();
  }, [onReset]);

  const downloadErrors = useCallback(() => {
    const errors = result?.errors || [];
    if (!errors.length) return;

    const csv =
      'Row,Identifier,Error Type,Error Message\n' +
      errors
        .map((e) =>
          [e.row ?? '', e.identifier ?? '', e.error_type, flattenApiError(e.message).join(' | ')]
            .map((f) => `"${String(f).replace(/"/g, '""')}"`)
            .join(',')
        )
        .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    Object.assign(document.createElement('a'), {
      href: url,
      download: `${modelName}_import_errors.csv`,
    }).click();
    URL.revokeObjectURL(url);
  }, [result, modelName]);

  // The envelope's buckets (info, cleared, unknown_identifiers, errors) are
  // NOT re-exposed here: ImportResult reads them off `result` directly, so
  // there is one reading of the envelope.
  return {
    file, loading, result, error,
    fileInputRef, selectFile, upload, reset,
    downloadTemplate, downloadErrors,
    clearBlanks, setClearBlanks,
  };
}
